import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { SyncConflict } from '@cleartask/shared';
import { descartarMinhaVersao, manterMinhaVersao, startAutoSync } from '../../lib/sync.js';
import { useToast } from '../../components/ui/Toast.js';
import { ConflictPanel } from './ConflictPanel.js';

/**
 * Liga a fila offline ao ciclo de vida da aba (spec 14).
 *
 * Mora dentro do `QueryClientProvider`, de onde vem a invalidacao, e dentro do
 * `ToastProvider`, que e como o usuario fica sabendo do que foi rejeitado.
 *
 * Deixou de nao renderizar nada quando o seletor de versoes nasceu: os
 * conflitos chegam aqui, no retorno da drenagem, e este e o unico ponto da
 * arvore que os ve em todas as telas.
 */
export function AutoSync() {
  const qc = useQueryClient();
  const { showToast } = useToast();

  /*
    Os conflitos vivem em memoria, e nao no IndexedDB junto da fila.

    Nao e descuido: um recarregamento perde a LISTA, mas nao perde nada de
    verdade — as operacoes continuam na fila e a proxima drenagem devolve os
    mesmos conflitos. Guardar uma copia seria um segundo lugar onde o conflito
    existe, e o primeiro trabalho de quem resolvesse um seria manter os dois
    de acordo.
  */
  const [conflitos, setConflitos] = useState<SyncConflict[]>([]);

  useEffect(
    () =>
      startAutoSync((resultado) => {
        // Invalida so quando algo de fato mudou. Invalidar a cada retorno de
        // foco refaria todas as consultas ativas sem motivo — exatamente o que
        // `refetchOnWindowFocus: false` foi configurado para evitar.
        const mudou =
          resultado.applied > 0 ||
          (resultado.changed?.tasks.length ?? 0) > 0 ||
          resultado.conflicts.length > 0;

        if (mudou) void qc.invalidateQueries();

        // A lista e SUBSTITUIDA, nao acumulada: cada drenagem reavalia a fila
        // inteira, entao o que nao voltou desta vez foi resolvido — por aqui ou
        // por outro aparelho. Acumular manteria na tela um conflito que ja nao
        // existe, e clicar nele nao faria nada.
        setConflitos(resultado.conflicts);

        // Uma operacao descartada pelo servidor precisa aparecer. Sem isto, a
        // escrita some da fila e da tela sem que ninguem saiba que ela nunca
        // chegou ao banco — a falha silenciosa mais cara deste desenho.
        if (resultado.rejected.length > 0) {
          const primeira = resultado.rejected[0];
          showToast({
            message:
              resultado.rejected.length === 1
                ? `Uma alteração não pôde ser salva: ${primeira?.message ?? ''}`
                : `${resultado.rejected.length} alterações não puderam ser salvas.`,
          });
        }
      }),
    [qc, showToast],
  );

  /** Tira o conflito da tela sem esperar a proxima drenagem. */
  const esquecer = useCallback((opId: string) => {
    setConflitos((atuais) => atuais.filter((c) => c.opId !== opId));
  }, []);

  const manter = useCallback(
    (conflito: SyncConflict) => {
      void (async () => {
        const resultado = await manterMinhaVersao(conflito.opId);
        void qc.invalidateQueries();

        // A resposta da drenagem manda: se o conflito voltou — porque o outro
        // aparelho escreveu DE NOVO nesse meio tempo —, ele continua na tela.
        // Remover na fe do clique esconderia um conflito ainda aberto.
        setConflitos(resultado.conflicts);
        if (!resultado.conflicts.some((c) => c.opId === conflito.opId)) {
          showToast({ message: 'Sua versão foi mantida' });
        }
      })();
    },
    [qc, showToast],
  );

  const descartar = useCallback(
    (conflito: SyncConflict) => {
      void (async () => {
        await descartarMinhaVersao(conflito.opId);
        esquecer(conflito.opId);
        // Invalidar traz de volta a versao do servidor para as telas que ainda
        // mostravam o patch otimista da escrita descartada.
        void qc.invalidateQueries();
        showToast({ message: 'Ficou a versão do servidor' });
      })();
    },
    [esquecer, qc, showToast],
  );

  return (
    <ConflictPanel conflitos={conflitos} onManterMinha={manter} onDescartarMinha={descartar} />
  );
}
